import React from 'react';
import Section from '../components/common/Section';
import { Helmet } from 'react-helmet-async';

const AppPrivacyPolicy = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy | ShaadiGlow</title>
                <meta name="description" content="Privacy Policy for ShaadiGlow App" />
            </Helmet>
            <Section className="bg-white pt-32 pb-20">
                <div className="max-w-4xl mx-auto text-gray-600">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
                    <p className="text-lg text-gray-500 mb-8"><strong>App Name:</strong> ShaadiGlow<br/>
                    <strong>Developer/Company Name:</strong> Mr. Tejas Galande<br/>
                    <strong>Contact Email:</strong> tejas5galande@gmail.com</p>
                    
                    <p className="text-sm text-gray-500 mb-8">Effective Date: 15 April 2026</p>

                    <div className="space-y-8">
                        <section>
                            <p className="leading-relaxed">
                                ShaadiGlow (“we,” “our,” or “us”) respects your privacy. This Privacy Policy explains how we collect, use, store, and protect your information when you use our mobile application. This app requests access to your device camera, so the policy should explain how and why camera access is used and how related data is handled.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
                            <p className="leading-relaxed mb-4">
                                We may collect the following types of information when you use the app:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Personal information you provide, such as your name, email address, phone number, profile details, or other account information.</li>
                                <li>Content you create or upload, such as photos, videos, profile images, or other media.</li>
                                <li>Device and usage information, such as app activity, crash reports, and performance data.</li>
                                <li>Notification-related identifiers if push notifications are enabled. Privacy policies for mobile apps with permissions should explain the categories of user data collected and processed.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Camera Permission</h2>
                            <p className="leading-relaxed mb-4">
                                Our app requests access to your device’s Camera permission so you can capture photos or videos directly within the app. Camera permission disclosures should explain the purpose of access and how the captured data is used.
                            </p>
                            <p className="leading-relaxed mb-4">
                                We use camera access only for features such as:
                            </p>
                            <ul className="list-disc pl-6 space-y-2 mb-4">
                                <li>Capturing profile photos.</li>
                                <li>Uploading portfolio images or videos.</li>
                                <li>Taking pictures for content submission or verification.</li>
                                <li>Any other in-app feature that clearly requires camera use.</li>
                            </ul>
                            <p className="leading-relaxed">
                                We do not access your camera in the background, and we only access it when you actively use a feature that requires it. Best practices for camera permission handling emphasize permission use only in direct response to a user action and with clear explanation.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                            <p className="leading-relaxed mb-4">
                                We may use your information to:
                            </p>
                            <ul className="list-disc pl-6 space-y-2 mb-4">
                                <li>Provide and improve app features.</li>
                                <li>Enable account creation and login.</li>
                                <li>Allow you to upload and manage photos or other content.</li>
                                <li>Personalize your experience.</li>
                                <li>Monitor app usage, stability, and performance.</li>
                                <li>Send important service-related notifications. Privacy policies for apps with camera and other permissions should explain how collected information is used and why.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Analytics, Crash Reporting, and Performance</h2>
                            <p className="leading-relaxed mb-4">
                                We may use third-party tools such as analytics, crash reporting, and performance monitoring services to understand app usage, diagnose errors, and improve reliability. Privacy policies should disclose third-party services involved in app analytics or technical monitoring.
                            </p>
                            <p className="leading-relaxed mb-4">
                                These tools may collect information such as:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Device type</li>
                                <li>App version</li>
                                <li>Usage events</li>
                                <li>Crash logs</li>
                                <li>Diagnostic and performance information</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing</h2>
                            <p className="leading-relaxed mb-4">
                                We do not sell your personal information. We may share limited information only in the following cases:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>With service providers that help us operate the app, such as cloud hosting, analytics, crash reporting, or notification services.</li>
                                <li>If required by law, regulation, legal request, or to protect our rights and users.</li>
                                <li>As part of a merger, acquisition, or business transfer, if applicable. Privacy policy guidance for apps with sensitive permissions recommends explaining whether data is shared and under what circumstances.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Storage and Retention</h2>
                            <p className="leading-relaxed mb-4">
                                Photos, videos, and other uploaded content may be stored on our servers or approved cloud service providers for as long as needed to provide app functionality, comply with legal obligations, resolve disputes, or enforce our agreements. Privacy policy guidance recommends clearly explaining retention of data collected or uploaded through app features such as camera use.
                            </p>
                            <p className="leading-relaxed">
                                If you delete content or request account deletion, we will take reasonable steps to delete or anonymize your data, subject to legal or operational requirements. Privacy policy guidance for camera-enabled apps recommends telling users how they can access or delete their data.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Choices</h2>
                            <p className="leading-relaxed mb-4">You may:</p>
                            <ul className="list-disc pl-6 space-y-2 mb-4">
                                <li>Deny camera permission, though some app features may not work properly.</li>
                                <li>Disable notifications through your device settings.</li>
                                <li>Contact us to update, access, or delete your personal data.</li>
                            </ul>
                            <p className="leading-relaxed">
                                Android privacy guidance and app permission best practices expect users to retain control over permission grants and data access.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children’s Privacy</h2>
                            <p className="leading-relaxed">
                                Our app is not intended for children under the age of 18. We do not knowingly collect personal information from children without proper legal basis or consent. Privacy policies commonly include a children’s privacy section where applicable.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Security</h2>
                            <p className="leading-relaxed">
                                We use reasonable administrative, technical, and organizational measures to protect your information. However, no system is completely secure, and we cannot guarantee absolute security of your data. Privacy policy guidance for camera-enabled apps recommends explaining security measures while avoiding promises of perfect protection.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
                            <p className="leading-relaxed">
                                We may update this Privacy Policy from time to time. When we do, we will update the “Effective Date” at the top of this page. Privacy policy templates and compliance guidance typically expect users to be informed that policies may change over time.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
                            <p className="leading-relaxed">
                                If you have questions about this Privacy Policy or your data, contact us at:<br /><br />
                                <strong>Email:</strong> <a href="mailto:tejas5galande@gmail.com" className="text-[#bebd19] hover:underline">tejas5galande@gmail.com</a><br />
                                <strong>Company/Developer Name:</strong> Mr. Tejas Galande.<br />
                                <strong>Address:</strong> Nagpur, Maharashtra.
                            </p>
                        </section>
                    </div>
                </div>
            </Section>
        </>
    );
};

export default AppPrivacyPolicy;
